import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { eventsApi, Event, schedulingLinksApi, proposalsApi, SchedulingLink, Proposal } from '../services/api';
import { Plus, Calendar, Users, Clock, MoreVertical, Send, X, Eye, Trash2, Link as LinkIcon, Inbox, ChevronRight, HelpCircle, Zap, ClipboardList, TrendingUp, CalendarCheck } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

type StatusFilter = 'all' | 'draft' | 'sent' | 'closed' | 'canceled';

export function DashboardPage() {
    const [events, setEvents] = useState<Event[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);

    const { user } = useAuth();

    // Scheduling links state
    const [schedulingLinks, setSchedulingLinks] = useState<SchedulingLink[]>([]);
    const [pendingProposals, setPendingProposals] = useState<Proposal[]>([]);

    // Help modal state
    const [showHelpModal, setShowHelpModal] = useState(false);

    useEffect(() => {
        loadEvents();
        loadSchedulingData();
    }, []);

    const loadEvents = async () => {
        try {
            setIsLoading(true);
            const data = await eventsApi.list();
            setEvents(data);
        } catch (err: any) {
            setError(err.message || 'Failed to load events');
        } finally {
            setIsLoading(false);
        }
    };

    const loadSchedulingData = async () => {
        try {
            const [links, proposals] = await Promise.all([
                schedulingLinksApi.list().catch(() => []),
                proposalsApi.list('pending').catch(() => [])
            ]);
            setSchedulingLinks(links);
            setPendingProposals(proposals);
        } catch (err) {
            console.error('Failed to load scheduling data:', err);
        }
    };

    const filteredEvents = statusFilter === 'all'
        ? events
        : events.filter(e => e.status === statusFilter);

    const handleCancel = async (id: string) => {
        if (!confirm('Are you sure you want to cancel this event?')) return;
        try {
            await eventsApi.cancel(id);
            loadEvents();
        } catch (err: any) {
            alert(err.message || 'Failed to cancel event');
        }
        setActionMenuOpen(null);
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this event?')) return;
        try {
            await eventsApi.delete(id);
            loadEvents();
        } catch (err: any) {
            alert(err.message || 'Failed to delete event');
        }
        setActionMenuOpen(null);
    };

    const getStatusBadge = (status: Event['status']) => {
        const badges: Record<Event['status'], string> = {
            draft: 'badge-draft',
            sent: 'badge-sent',
            closed: 'badge-closed',
            canceled: 'badge-canceled',
        };
        return `badge ${badges[status]}`;
    };

    // Calculate stats
    const totalEvents = events.length;
    const activeLinks = schedulingLinks.filter(l => l.is_active).length;
    const pendingCount = pendingProposals.length;
    const totalResponses = events.reduce((sum, e) => sum + (e.response_count || 0), 0);
    const totalRecipients = events.reduce((sum, e) => sum + (e.recipient_count || 0), 0);
    const responseRate = totalRecipients > 0 ? Math.round((totalResponses / totalRecipients) * 100) : 0;

    if (isLoading) {
        return (
            <div className="page container">
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    return (
        <div className="page container">
            {/* Hero Section */}
            <div className="dashboard-hero">
                <div className="dashboard-hero-content">
                    <div>
                        <h1 className="dashboard-hero-title">
                            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
                        </h1>
                        <p className="dashboard-hero-subtitle">
                            Manage your events and scheduling links from one place
                        </p>
                    </div>
                    <div className="dashboard-hero-actions">
                        <button
                            onClick={() => setShowHelpModal(true)}
                            className="btn btn-ghost"
                            title="Help"
                            style={{
                                color: 'rgba(255,255,255,0.8)',
                                background: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(255,255,255,0.2)'
                            }}
                        >
                            <HelpCircle size={18} />
                        </button>
                        <Link to="/events/new" className="btn btn-primary">
                            <Plus size={18} />
                            Create Meeting Poll
                        </Link>
                    </div>
                </div>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            {/* Stats Grid */}
            <div className="dashboard-stats">
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: 'var(--color-primary-light)', color: 'var(--color-primary)' }}>
                        <Calendar size={22} />
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{totalEvents}</span>
                        <span className="stat-label">Meeting Polls</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
                        <LinkIcon size={22} />
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{activeLinks}</span>
                        <span className="stat-label">Active Links</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{
                        background: pendingCount > 0 ? '#fef3c7' : 'var(--color-gray-100)',
                        color: pendingCount > 0 ? '#f59e0b' : 'var(--color-gray-500)'
                    }}>
                        <Inbox size={22} />
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{pendingCount}</span>
                        <span className="stat-label">Pending Proposals</span>
                    </div>
                    {pendingCount > 0 && (
                        <span className="stat-badge">Action needed</span>
                    )}
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#dcfce7', color: '#22c55e' }}>
                        <TrendingUp size={22} />
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{responseRate}%</span>
                        <span className="stat-label">Response Rate</span>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="dashboard-quick-actions">
                <Link to="/scheduling-links" className="quick-action-card">
                    <div className="quick-action-icon">
                        <LinkIcon size={24} />
                    </div>
                    <div className="quick-action-content">
                        <h3>Scheduling Links</h3>
                        <p>{schedulingLinks.length} link{schedulingLinks.length !== 1 ? 's' : ''} created</p>
                    </div>
                    <ChevronRight size={20} className="quick-action-arrow" />
                </Link>

                <Link to="/proposals" className="quick-action-card" style={{
                    borderColor: pendingProposals.length > 0 ? 'var(--color-primary)' : undefined,
                    background: pendingProposals.length > 0 ? 'linear-gradient(135deg, rgba(0, 180, 216, 0.05) 0%, rgba(0, 180, 216, 0.1) 100%)' : undefined
                }}>
                    <div className="quick-action-icon" style={{
                        background: pendingProposals.length > 0 ? 'var(--color-primary)' : undefined,
                        color: pendingProposals.length > 0 ? 'white' : undefined
                    }}>
                        <Inbox size={24} />
                    </div>
                    <div className="quick-action-content">
                        <h3>Meeting Proposals</h3>
                        <p style={{ color: pendingProposals.length > 0 ? 'var(--color-primary)' : undefined }}>
                            {pendingProposals.length > 0
                                ? `${pendingProposals.length} pending request${pendingProposals.length !== 1 ? 's' : ''}`
                                : 'No pending requests'
                            }
                        </p>
                    </div>
                    <ChevronRight size={20} className="quick-action-arrow" />
                </Link>
            </div>

            {/* Meeting Polls Section */}
            <div className="dashboard-events-section">
                <div className="dashboard-events-header">
                    <h2>My Meeting Polls</h2>
                    <div className="dashboard-filter-tabs">
                        {(['all', 'draft', 'sent', 'closed', 'canceled'] as StatusFilter[]).map((status) => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`filter-tab ${statusFilter === status ? 'active' : ''}`}
                            >
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Events Grid */}
                {filteredEvents.length === 0 ? (
                    <div className="empty-state-card">
                        <CalendarCheck size={56} className="empty-state-icon" />
                        <h3>No meeting polls yet</h3>
                        <p>Create your first meeting poll to propose times and let recipients vote</p>
                        <Link to="/events/new" className="btn btn-secondary">
                            <Plus size={18} />
                            Create Meeting Poll
                        </Link>
                    </div>
                ) : (
                    <div className="events-grid">
                        {filteredEvents.map((event) => (
                            <div key={event.id} className="event-card">
                                {/* Action Menu */}
                                <div style={{ position: 'absolute', top: '16px', right: '16px' }}>
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={() => setActionMenuOpen(actionMenuOpen === event.id ? null : event.id)}
                                    >
                                        <MoreVertical size={18} />
                                    </button>
                                    {actionMenuOpen === event.id && (
                                        <div className="event-action-menu">
                                            <Link
                                                to={`/events/${event.id}`}
                                                className="event-action-item"
                                            >
                                                <Eye size={16} /> View Details
                                            </Link>
                                            {event.status === 'draft' && (
                                                <Link
                                                    to={`/events/${event.id}/edit`}
                                                    className="event-action-item"
                                                >
                                                    <Send size={16} /> Edit & Send
                                                </Link>
                                            )}
                                            {event.status === 'sent' && (
                                                <button
                                                    onClick={() => handleCancel(event.id)}
                                                    className="event-action-item danger"
                                                >
                                                    <X size={16} /> Cancel Poll
                                                </button>
                                            )}
                                            {event.status !== 'sent' && (
                                                <button
                                                    onClick={() => handleDelete(event.id)}
                                                    className="event-action-item danger"
                                                >
                                                    <Trash2 size={16} /> Delete Poll
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Event Content */}
                                <div style={{ marginBottom: 'var(--space-3)' }}>
                                    <span className={getStatusBadge(event.status)}>
                                        {event.status}
                                    </span>
                                </div>

                                <h3 className="event-card-title">
                                    <Link to={`/events/${event.id}`} style={{ color: 'inherit' }}>
                                        {event.event_name}
                                    </Link>
                                </h3>

                                <div className="event-card-meta">
                                    <span>
                                        <Users size={14} />
                                        {event.recipient_count} recipient{event.recipient_count !== 1 ? 's' : ''}
                                    </span>
                                    <span>
                                        <Clock size={14} />
                                        {event.duration_minutes} min
                                    </span>
                                </div>

                                {event.status === 'sent' && (
                                    <div className="event-response-bar">
                                        <div className="response-progress">
                                            <div
                                                className="response-progress-fill"
                                                style={{ width: `${event.recipient_count > 0 ? (event.response_count / event.recipient_count) * 100 : 0}%` }}
                                            />
                                        </div>
                                        <span className="response-text">
                                            <strong>{event.response_count}</strong> of {event.recipient_count} responded
                                        </span>
                                    </div>
                                )}

                                <div className="event-card-footer">
                                    Created {format(new Date(event.created_at), 'MMM d, yyyy')}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Help Modal */}
            {showHelpModal && (
                <HelpModal onClose={() => setShowHelpModal(false)} />
            )}
        </div>
    );
}

// Help Modal Component
function HelpModal({ onClose }: { onClose: () => void }) {
    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h2>
                        <HelpCircle size={24} style={{ color: 'var(--color-primary)' }} />
                        How Let's Meet Works
                    </h2>
                    <button onClick={onClose} className="btn btn-ghost btn-sm">
                        <X size={20} />
                    </button>
                </div>

                {/* Meeting Polls Section */}
                <div className="help-section">
                    <div className="help-section-header">
                        <div className="help-icon">
                            <Calendar size={20} />
                        </div>
                        <h3>Meeting Polls</h3>
                    </div>
                    <p>Meeting Polls are <strong>voting-based scheduling</strong> for group meetings.</p>
                    <ul>
                        <li>Propose multiple time slots that work for you</li>
                        <li>Recipients vote on which times work for them</li>
                        <li>See vote counts for each time slot</li>
                        <li>Book the most popular time and send calendar invites</li>
                        <li>Customize with descriptions, reminders, and custom form fields</li>
                    </ul>
                </div>

                {/* Scheduling Links Section */}
                <div className="help-section">
                    <div className="help-section-header">
                        <div className="help-icon">
                            <LinkIcon size={20} />
                        </div>
                        <h3>Scheduling Links</h3>
                    </div>
                    <p>Scheduling links are <strong>reusable booking pages</strong> you can share with anyone. They automatically check your calendar for availability.</p>

                    <div className="help-cards">
                        <div className="help-card">
                            <div className="help-card-header">
                                <ClipboardList size={18} />
                                <strong>Propose Times</strong>
                            </div>
                            <p>Invitees suggest meeting times for you to review. You approve or decline each request before any meeting is scheduled.</p>
                        </div>

                        <div className="help-card instant">
                            <div className="help-card-header">
                                <Zap size={18} />
                                <strong>Instant Booking</strong>
                            </div>
                            <p>Invitees book directly on your calendar. A Microsoft Teams meeting is created automatically—no approval needed.</p>
                        </div>
                    </div>
                </div>

                {/* Meeting Proposals Section */}
                <div className="help-section">
                    <div className="help-section-header">
                        <div className="help-icon">
                            <Inbox size={20} />
                        </div>
                        <h3>Meeting Proposals</h3>
                    </div>
                    <p>When someone uses your "Propose Times" scheduling link, their request appears here. Review the proposed times and approve the one that works best for you.</p>
                </div>

                <div className="modal-footer">
                    <button onClick={onClose} className="btn btn-primary">
                        Got it!
                    </button>
                </div>
            </div>
        </div>
    );
}
